import {Injectable} from "@angular/core";
import {pruneEmpty, VisbosService} from "../../core/visbos-core/graphql/visbos.service";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../shared/redux-store/store";
import {SessionActions} from "../../shared/actions/session.actions";
import gql from "graphql-tag";
import {Observable} from "rxjs/Observable";
import {ApolloQueryResult} from "apollo-client";
import {PrintedSheet} from "../../shared/view-model/store-management/printed-sheet.type";
import {StickerSheet} from "../../shared/view-model/store-management/sticker-sheet.type";
import {StickerFormat} from "../../shared/view-model/store-management/sticker-format.type";
import {
  CreateStickerFormatMutation,
  DeleteStickerFormatMutation,
  PrintCustomStickerSheetsOnOnePageMutation,
  StickerFormatInput,
  UpdateStickerFormatMutation
} from "../../shared/graphql/graphql.types";

export const medicineFragments = {
  stickerFormat: gql`
    fragment StickerFormatFields on StickerFormat {
      cuid label is_editable
      created_at updated_at
      substance_string
      dosage_string dosage_only_string no_dosage_string
      color_mode
      administration_unit {cuid label}
    }
  `
};

export const createMedicineStickersPdf = gql`
  query CreateMedicineStickersPdf($medicineCuids: [String], $sheetCuids: [String],
  $stickerFormatCuid: String, $printedSheetCuid:String, $trainingMode: Boolean) {
    generateMedicineStickersPDF(medicine_cuids: $medicineCuids, customStickerSheetCuids: $sheetCuids,
      stickerFormatCuid: $stickerFormatCuid, printedSheetCuid: $printedSheetCuid, trainingMode: $trainingMode)
  }
`;

export const printCustomStickerSheetsOnOnePage = gql`
  mutation PrintCustomStickerSheetsOnOnePage($customSheetsCuids:[String], $printOnOnePage: Boolean, $trainingMode: Boolean) {
    customStickerSheetsPdf(customSheetsCuids: $customSheetsCuids, printOnOnePage: $printOnOnePage, trainingMode: $trainingMode)
  }
`;

export const fetchStickerSheetsQuery = gql`
  query FetchStickerSheetsQuery($adminUnitCuid: String) {
    stickerSheets(adminUnitCuid: $adminUnitCuid) {
      cuid label is_editable
      created_at updated_at
      administration_unit {cuid label}
      stickers {
        cuid label is_editable
        sheet_row sheet_column
      }
      creator {cuid label}
    }
  }
`;

export const fetchPrintedSheetsQuery = gql`
  query FetchPrintedSheetsQuery($adminUnitCuid: String, $sheetTypeCuid: String) {
    printedSheets(adminUnitCuid: $adminUnitCuid, sheetTypeCuid: $sheetTypeCuid) {
      cuid label is_editable
      created_at updated_at
      page_width page_height
      sticker_width sticker_height
      rows columns
    }
  }
`;

export const getStickerFormatsQuery = gql`
  query GetStickerFormatsQuery($adminUnitCuid: String) {
    stickerFormats(adminUnitCuid: $adminUnitCuid) {
      ...StickerFormatFields
    }
  }
  ${medicineFragments.stickerFormat}
`;

export const createCustomStickersSheetMutation = gql`
  mutation CreateCustomStickersSheetMutation($sheetData: StickerSheetInput!) {
    createCustomStickersSheet(sheetData: $sheetData) {
      cuid label is_editable
      created_at updated_at
      stickers {
        cuid label
        is_editable created_at updated_at
        sheet_row sheet_column
      }
    }
  }
`;

export const deleteCustomStickersSheetMutation = gql`
  mutation DeleteCustomStickersSheetMutation($sheetCuid: String!) {
    deleteCustomStickersSheet(sheetCuid: $sheetCuid)
  }
`;

export const generateStickerPreviewQuery = gql`
  query GenerateStickerPreviewQuery($medicineCuid: String!, $stickerFormatCuid: String!, $printedSheetCuid: String!) {
    generateMedicineStickerPreview(medicine_cuid: $medicineCuid, stickerFormatCuid: $stickerFormatCuid, printedSheetCuid: $printedSheetCuid)
  }
`;

export const createStickerFormatMutation = gql`
  mutation CreateStickerFormat($formatData: StickerFormatInput!) {
    createStickerFormat(formatData: $formatData) {
      ...StickerFormatFields
    }
  }
  ${medicineFragments.stickerFormat}
`;

export const updateStickerFormatMutation = gql`
  mutation UpdateStickerFormat($cuid: String!, $formatData: StickerFormatInput!) {
    updateStickerFormat(cuid: $cuid, formatData: $formatData) {
      ...StickerFormatFields
    }
  }
  ${medicineFragments.stickerFormat}
`;

export const deleteStickerFormatMutation = gql`
  mutation DeleteStickerFormat($cuid: String!) {
    deleteStickerFormat(cuid: $cuid)
  }
`;

export interface GenerateStickerPreviewResult {
  generateMedicineStickerPreview: string;
}

export interface PrintedSheetsResult {
  printedSheets: PrintedSheet[];
}

export interface StickerSheetsResult {
  stickerSheets: StickerSheet[];
}

export interface StickerFormatsResult {
  stickerFormats: StickerFormat[];
}

@Injectable()
export class MedicineStickersService extends VisbosService {

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }


  public createMedicineStickersPdf(medicineCuids?: Array<string>, stickerSheetCuids?: Array<string>,
                                   printedSheetCuid?: string, stickerFormatCuid?: string, trainingMode?: boolean): ApolloQueryObservable<{ generateMedicineStickersPDF: string }> {
    return this.fetchItems({
      query: createMedicineStickersPdf,
      variables: {
        medicineCuids: medicineCuids,
        sheetCuids: stickerSheetCuids,
        stickerFormatCuid: stickerFormatCuid,
        printedSheetCuid: printedSheetCuid,
        trainingMode: trainingMode
      },
      fetchPolicy: "cache-first"
    })
  }

  public generateCustomStickersSheetPdf(sheetsCuids: string[], printOnOnePage: boolean = false, trainingMode?: boolean): Observable<ApolloQueryResult<PrintCustomStickerSheetsOnOnePageMutation>> {
    return this.mutate({
      mutation: printCustomStickerSheetsOnOnePage,
      variables: {
        customSheetsCuids: sheetsCuids,
        printOnOnePage: printOnOnePage,
        trainingMode: trainingMode
      }
    });
  }

  public getPrintedSheets(adminUnitCuid?: string | Observable<string>, type?: string): ApolloQueryObservable<PrintedSheetsResult> {
    return this.fetchItems({
      query: fetchPrintedSheetsQuery,
      variables: {
        adminUnitCuid: adminUnitCuid,
        sheetTypeCuid: type
      }
    });
  }

  public getStickerFormats(adminUnitCuid?: string | Observable<string>): ApolloQueryObservable<StickerFormatsResult> {
    return this.fetchItems({
      query: getStickerFormatsQuery,
      variables: {
        adminUnitCuid: adminUnitCuid
      }
    });
  }

  public createStickerFormat(formatData: StickerFormatInput): Observable<ApolloQueryResult<CreateStickerFormatMutation>> {
    return this.mutate({
      mutation: createStickerFormatMutation,
      variables: {
        formatData: formatData
      },
      refetchQueries: ['GetStickerFormatsQuery']
    });
  }

  public updateStickerFormat(cuid: string, formatData: StickerFormatInput): Observable<ApolloQueryResult<UpdateStickerFormatMutation>> {
    return this.mutate({
      mutation: updateStickerFormatMutation,
      variables: {
        cuid: cuid,
        formatData: formatData
      }
    });
  }

  public deleteStickerFormat(cuid: string): Observable<ApolloQueryResult<DeleteStickerFormatMutation>> {
    return this.mutate({
      mutation: deleteStickerFormatMutation,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['GetStickerFormatsQuery']
    });
  }

  public getStickerSheets(adminUnitCuid?: string | Observable<string>): ApolloQueryObservable<StickerSheetsResult> {
    return this.fetchItems({
      query: fetchStickerSheetsQuery,
      variables: {
        adminUnitCuid: adminUnitCuid
      }
    });
  }

  public saveCustomStickersSheet(sheetInput): Observable<ApolloQueryResult<any>> {

    let processedInput = pruneEmpty(sheetInput);

    return this.mutate({
      mutation: createCustomStickersSheetMutation,
      variables: {
        sheetData: processedInput
      },
      refetchQueries: ['FetchStickerSheetsQuery']
    });
  }

  public deleteCustomStickersSheet(sheetCuid: string): Observable<ApolloQueryResult<any>> {
    return this.mutate({
      mutation: deleteCustomStickersSheetMutation,
      variables: {
        sheetCuid: sheetCuid
      },
      refetchQueries: ['FetchStickerSheetsQuery']
    });
  }

  public generateStickerPreview(medicineCuid: string | Observable<string>,
                                printedSheetCuid: string | Observable<string>,
                                stickerFormatCuid: string | Observable<string>): ApolloQueryObservable<GenerateStickerPreviewResult> {
    return this.fetchItems({
      query: generateStickerPreviewQuery,
      variables: {
        medicineCuid: medicineCuid,
        printedSheetCuid: printedSheetCuid,
        stickerFormatCuid: stickerFormatCuid
      },
      fetchPolicy: "network-only"
    })
  }

}



// WEBPACK FOOTER //
// ./src/app/medicine-stickers/services/medicine-stickers.service.ts